import Pusher, { Channel } from 'pusher-js';
import { useEffect, useRef, useState } from 'react';
import { AuthService } from '../services';
import { reportMonitoredError } from '../services/sentry';

interface UsePusherOptions {
  /** Pusher app key */
  appKey: string;
  /** Pusher cluster */
  cluster: string;
  /** Whether to enable debug logging */
  debug?: boolean;
  /** Whether to enable the Pusher connection (default: false) */
  enabled?: boolean;
}

interface UsePusherReturn {
  /** Pusher instance */
  pusher: Pusher | null;
  /** Whether Pusher is connected */
  isConnected: boolean;
  /** Subscribe to a channel */
  subscribe: (channelName: string) => Channel | null;
  /** Unsubscribe from a channel */
  unsubscribe: (channelName: string) => void;
  /** Get channel if already subscribed */
  getChannel: (channelName: string) => Channel | undefined;
}

export const usePusher = (options: UsePusherOptions): UsePusherReturn => {
  const { appKey, cluster, debug = false, enabled = false } = options;

  const [pusher, setPusher] = useState<Pusher | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const subscribedChannels = useRef<Map<string, Channel>>(new Map());
  const pusherRef = useRef<Pusher | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let isMounted = true;
    let pusherInstance: Pusher | null = null;

    const initializePusher = async () => {
      if (debug) {
        Pusher.logToConsole = true;
      }

      const authToken = AuthService.getAuth()?.auth_token;
      if (!authToken) {
        console.warn('[Pusher] Not initializing: no auth token');
        return;
      }

      // Was `await AuthService.getProfile(...)`, which passed the *whole profile
      // object* as this param — so the user's name and email were serialized into
      // a query string, and a rejected profile fetch threw before `new Pusher`
      // ever ran, silently killing every realtime event.
      const participantUuid =
        AuthService.getLocalProfile()?.uuid ?? (await AuthService.getProfile(authToken)).user?.uuid;

      if (!participantUuid) {
        console.warn('[Pusher] Not initializing: no participant uuid');
        return;
      }

      pusherInstance = new Pusher(appKey, {
        cluster,
        authEndpoint: `${process.env.REACT_APP_PUBLIC_APPOINTMENTS_API_BASE_URL}/rtm/auth`,
        auth: {
          params: { participantUuid },
        },
      });

      pusherRef.current = pusherInstance;

      if (isMounted) {
        setPusher(pusherInstance);
      }

      // Connection state handlers
      pusherInstance.connection.bind('connected', () => {
        console.log('[Pusher] Connected');
        if (isMounted) {
          setIsConnected(true);
        }
      });

      pusherInstance.connection.bind('disconnected', () => {
        console.log('[Pusher] Disconnected');
        if (isMounted) {
          setIsConnected(false);
        }
      });

      pusherInstance.connection.bind('error', (err: any) => {
        console.error('[Pusher] Connection error:', err);
      });
    };

    // Was a floating call. An unhandled rejection here left `pusherRef` null and
    // every subsequent subscribe logging "Pusher not initialized" — no SMS,
    // voicemail, transfer, or agent-status events, with nothing shown to the user.
    initializePusher().catch((error) => {
      console.error('[Pusher] Initialization failed:', error);
      reportMonitoredError({ error });
    });

    // Captured here rather than read in the cleanup, so we clean up the map this
    // effect actually populated.
    const channels = subscribedChannels.current;

    // Cleanup on unmount
    return () => {
      isMounted = false;
      console.log('[Pusher] Cleaning up connection');

      // Unsubscribe from all channels
      if (pusherInstance) {
        channels.forEach((channel, channelName) => {
          pusherInstance!.unsubscribe(channelName);
        });
        channels.clear();

        // Disconnect Pusher
        pusherInstance.disconnect();
        pusherRef.current = null;
      }
    };
  }, [appKey, cluster, debug, enabled]);

  const subscribe = (channelName: string): Channel | null => {
    if (!pusherRef.current) {
      console.warn('[Pusher] Cannot subscribe - Pusher not initialized');
      return null;
    }

    // Return existing channel if already subscribed
    if (subscribedChannels.current.has(channelName)) {
      return subscribedChannels.current.get(channelName)!;
    }

    // Subscribe to new channel
    console.log(`[Pusher] Subscribing to channel: ${channelName}`);
    const channel = pusherRef.current.subscribe(channelName);

    // Handle subscription errors
    channel.bind('pusher:subscription_error', (status: any) => {
      console.error(`[Pusher] Subscription error for ${channelName}:`, status);
    });

    channel.bind('pusher:subscription_succeeded', () => {
      console.log(`[Pusher] Successfully subscribed to ${channelName}`);
    });

    subscribedChannels.current.set(channelName, channel);
    return channel;
  };

  const unsubscribe = (channelName: string): void => {
    if (!pusherRef.current) return;

    console.log(`[Pusher] Unsubscribing from channel: ${channelName}`);
    pusherRef.current.unsubscribe(channelName);
    subscribedChannels.current.delete(channelName);
  };

  const getChannel = (channelName: string): Channel | undefined => {
    return subscribedChannels.current.get(channelName);
  };

  return {
    pusher,
    isConnected,
    subscribe,
    unsubscribe,
    getChannel,
  };
};
